import React from "react"
import { graphql } from "gatsby"
import styled from "styled-components"

import Layout from "../layouts/Layout"
import { Container, Row, Col } from "../components/Grid"
import Snippet from "../components/Snippet"
import Cta from "../components/Cta"
import Seo from "../components/Seo"

import { media, mixins } from "../styles"

import NavigationGrid from "../components/NavigationGrid"

// Query fragment is available globally and can be used on any product pages
// This seems like a good place to define it
export const productQuery = graphql`
  fragment ProductData on ContentfulProduct {
    name
    slug
    claim
    iconSmall {
      ...ContentfulImageData
    }
    descriptionLong {
      childMarkdownRemark {
        html
      }
    }
    benefits {
      title
      icon {
        ...ContentfulImageData
      }
      description {
        childMarkdownRemark {
          html
        }
      }
    }
    testimonials {
      authorName
      authorPhoto {
        ...ContentfulImageData
        gatsbyImageData(
          cropFocus: FACE
          resizingBehavior: THUMB
          height: 240
          width: 240
          quality: 90
          layout: CONSTRAINED
          placeholder: BLURRED
        )
      }
      authorTitle
      company {
        logo {
          ...ContentfulImageData
          gatsbyImageData(height: 25, width: 140, layout: CONSTRAINED)
        }
      }
      thumbnail {
        ...ContentfulImageData
        gatsbyImageData(
          height: 428
          width: 571
          quality: 75
          layout: CONSTRAINED
          placeholder: BLURRED
        )
      }
      text {
        text
      }
      youtubeURL
    }
    features {
      code {
        code
      }
      description {
        childMarkdownRemark {
          html
        }
      }
      name
    }
    useCases {
      description {
        childMarkdownRemark {
          html
        }
      }
      iconSmall {
        ...ContentfulImageData
      }
      iconLarge {
        ...ContentfulImageData
      }
      icon: iconLarge {
        ...ContentfulImageData
      }
      name
      image {
        ...ContentfulImageData
        gatsbyImageData(
          height: 1360 # we want 680 but the plugin doesn't respect pixel densities when only height is set
          quality: 85
          layout: CONSTRAINED
          placeholder: BLURRED
        )
      }
      imageAlignment
    }
    clientGroup1Name
    clientGroup1 {
      name
      url
      logo {
        ...ContentfulImageData
        gatsbyImageData(
          height: 45
          width: 100
          quality: 75
          layout: CONSTRAINED
        )
      }
      logoAligned {
        ...ContentfulImageData
        gatsbyImageData(
          height: 45
          width: 100
          quality: 75
          layout: CONSTRAINED
        )
      }
      logoInlined {
        ...ContentfulImageData
        gatsbyImageData(
          height: 45
          width: 100
          quality: 75
          layout: CONSTRAINED
        )
      }
    }
    clientGroup2Name
    clientGroup2 {
      name
      logo {
        ...ContentfulImageData
        gatsbyImageData(
          height: 45
          width: 100
          quality: 75
          layout: CONSTRAINED
        )
      }
      logoAligned {
        ...ContentfulImageData
        gatsbyImageData(
          height: 45
          width: 100
          quality: 75
          layout: CONSTRAINED
        )
      }
      logoInlined {
        ...ContentfulImageData
        gatsbyImageData(
          height: 45
          width: 100
          quality: 75
          layout: CONSTRAINED
        )
      }
      url
    }
    ctaHeader
    ctaLinkText
    ctaLinkUrl
  }
`

const Wrapper = styled.div`
  background: var(--color-grey-light);
  padding: 50px 10px;

  @media ${media.md} {
    padding: 50px 10px 70px;
  }

  @media ${media.lg} {
    padding: 70px 10px 90px;
  }

  @media ${media.xl} {
    padding: 95px 10px 120px;
  }
`

const Header = styled.div`
  text-align: center;
  margin: 0 0 50px;

  h1 {
    ${mixins.title2}
    margin: 0 0 20px;

    @media ${media.xl} {
      margin: 0 0 40px;
    }
  }

  p {
    ${mixins.copy2}
  }

  @media ${media.lg} {
    margin: 0 0 70px;
    padding: 0 20px;
  }

  @media ${media.xl} {
    margin: 0 0 110px;
    padding: 0 60px;
  }
`

const SubHeader = styled.h2`
  ${mixins.title5}
  line-height: 1;
  margin: 0 0 10px;

  @media ${media.lg} {
    margin: 0 0 15px;
  }

  @media ${media.xl} {
    margin: 0 0 5px;
  }
`

const Services = styled.div`
  padding: 0 10px;

  @media ${media.sm} {
    padding: 0 30px;
  }

  @media ${media.lg} {
    padding: 0 60px;
  }

  @media ${media.xl} {
    padding: 0;
  }
`

export const pageQuery = graphql`
  query ServicesPageQuery($language: String, $originalPath: String) {
    contentfulPageMetadata(
      pagePath: { eq: $originalPath }
      node_locale: { eq: $language }
    ) {
      ...PageMetadata
    }
    allContentfulProductCollection(
      filter: {
        visible: { eq: true }
        node_locale: { eq: $language }
        isOld: { ne: true }
      }
      sort: { sortOrder: ASC }
    ) {
      nodes {
        node_locale
        id
        icon: iconSmall {
          ...ContentfulImageData
        }
        iconLarge {
          ...ContentfulImageData
        }
        name
        items: products {
          ... on ContentfulProduct {
            name
            slug
            iconSmall {
              ...ContentfulImageData
            }
            iconLarge {
              ...ContentfulImageData
            }
          }
        }
      }
    }
  }
`

const ServicesPage = (props) => {
  const pageMetadata = props.data.contentfulPageMetadata
  const productCollections = props.data.allContentfulProductCollection.nodes

  return (
    <Layout pageId="services">
      <Seo {...pageMetadata} />
      <Container>
        <Wrapper>
          <Row>
            <Col md="10" mdOffset="1" lg="8" lgOffset="2">
              <Header>
                <SubHeader>
                  <Snippet id="servicesSubheader" field="header" />
                </SubHeader>
                <h1>
                  <Snippet id="servicesHeader" field="header" />
                </h1>
                <Snippet id="servicesHeader" field="text" md />
              </Header>
            </Col>
          </Row>
          <Row>
            <Col xl="10" xlOffset="1">
              <Services>
                <NavigationGrid
                  basePath="/services/"
                  headerLevel={2}
                  items={productCollections}
                  mode="serviceoverview"
                />
              </Services>
            </Col>
          </Row>
        </Wrapper>
      </Container>
      <Cta
        header={Snippet({
          id: "servicesCTA",
          field: "header",
        })}
        linkText={Snippet({
          id: "servicesCTA",
          field: "linkText",
        })}
        linkUrl={Snippet({
          id: "servicesCTA",
          field: "linkUrl",
        })}
      />
    </Layout>
  )
}

export default ServicesPage
